import {
  MutationCache,
  QueryCache,
  QueryClient
} from '@tanstack/react-query';

import { newError } from '@/services/errors/errors';

/**
 * Centralised TanStack Query client.
 *
 * - Errors raised by queries/mutations are reported through `newError` so
 *   they reach Sentry exactly like the rest of the app's error reporting.
 *   Per-query polling intervals override `staleTime`, so the global
 *   `staleTime: 60_000` still allows real-time hooks (generations) to
 *   refetch as configured.
 */
export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      newError('QC-Q', error, false, {
        sentryDisabled: false,
        isConsoleLogged: false
      });
      // Stash a structured breadcrumb so the Sentry event has the failing
      // query key, not just the error message.
      newError(
        'QC-Q-CTX',
        `Query failed: ${JSON.stringify(query.queryKey)}`,
        false,
        { sentryDisabled: true, isConsoleLogged: false }
      );
    }
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      newError('QC-M', error, false, {
        sentryDisabled: false,
        isConsoleLogged: false
      });
      newError(
        'QC-M-CTX',
        `Mutation failed: ${JSON.stringify(mutation.options.mutationKey ?? 'anon')}`,
        false,
        { sentryDisabled: true, isConsoleLogged: false }
      );
    }
  }),
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute default
      gcTime: 5 * 60 * 1000, // 5 minutes garbage collection
      retry: 1,
      refetchOnWindowFocus: false
    }
  }
});
